import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alan, BosDurum, Buton, Iskelet, Kart, MetinAlani, Rozet, Secim } from "@koclukpanel/ui";
import {
  ApiHatasi,
  harcamaGir,
  kategoriTanimla,
  mesguliyetEkle,
  ogrenciListesi,
  talepKategorileri,
} from "./api";

/**
 * ÜÇ KÜÇÜK FORM — hepsinin ucu vardı, hiçbirinin ekranı yoktu.
 *
 * Ayrı ayrı dosya açmak yerine tek dosyada duruyorlar: üçü de tek
 * formluk, birbirine benzeyen ve BAŞKA ekranların içine yerleşen
 * parçalar. Her biri için ayrı bir ekran açmak, menüyü üç girdi
 * uzatır ve kullanıcıyı işini yaptığı yerden koparırdı.
 */

// =====================================================================
// TALEP KATEGORİSİ TANIMLAMA — matris §5.8
// =====================================================================

/**
 * `kararIzni` BU KATEGORİDEKİ TALEBİ KARARA BAĞLAMAK İÇİN GEREKEN
 * İZİN KODU — kategoriyi tanımlayanın kendi izni değil.
 *
 * Kural role değil KAYDA bağlı: finans yalnızca ödeme taleplerini
 * karara bağlar ve bu, kodda rol kontrolüyle değil bu alanla
 * sağlanıyor (CLAUDE.md kural 1). Sunucu kodu KATALOĞA karşı
 * doğruluyor; tanınmayan kod kategoriyi açmıyor.
 */
const SIK_KULLANILAN_IZINLER = [
  ["program.degisiklik.karara_bagla", "Program değişikliği — uzman karara bağlar"],
  ["finans.tahsilat.isle", "Ödeme/finans — finans karara bağlar"],
  ["operasyon.talep.karara_bagla", "Genel operasyon talebi"],
  ["kisi.ogrenci.duzenle", "Öğrenci kaydıyla ilgili"],
] as const;

export function TalepKategorileri() {
  const qc = useQueryClient();
  const [acik, setAcik] = useState(false);
  const [kod, setKod] = useState("");
  const [ad, setAd] = useState("");
  const [aciklama, setAciklama] = useState("");
  const [kararIzni, setKararIzni] = useState<string>(SIK_KULLANILAN_IZINLER[0][0]);
  const [acabilen, setAcabilen] = useState<"STUDENT" | "PARENT" | "BOTH">("BOTH");
  const [bilgi, setBilgi] = useState<string | null>(null);

  const liste = useQuery({
    queryKey: ["talep-kategorileri"],
    queryFn: talepKategorileri,
    retry: false,
  });

  const tanimla = useMutation({
    mutationFn: () =>
      kategoriTanimla({
        kod: kod.trim(),
        ad: ad.trim(),
        aciklama: aciklama.trim() || null,
        kararIzni,
        acabilen,
      }),
    onSuccess: async () => {
      setBilgi(`"${ad.trim()}" kategorisi tanımlandı.`);
      setKod("");
      setAd("");
      setAciklama("");
      await qc.invalidateQueries({ queryKey: ["talep-kategorileri"] });
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <Kart
        baslik="Talep kategorileri"
        aciklama="Öğrenci ve velinin hangi başlıklarda talep açabileceğini ve her başlığı KİMİN karara bağlayacağını belirler."
        eylem={
          <Buton gorunum="ikincil" onClick={() => setAcik((a) => !a)} aria-expanded={acik}>
            {acik ? "Formu kapat" : "Kategori tanımla"}
          </Buton>
        }
      >
        {liste.isPending ? (
          <Iskelet satir={2} />
        ) : liste.isError ? (
          <BosDurum
            ton="hata"
            baslik="Kategoriler alınamadı"
            aciklama={liste.error instanceof ApiHatasi ? liste.error.message : undefined}
          />
        ) : liste.data.length === 0 ? (
          <BosDurum
            baslik="Kategori tanımlanmamış"
            /*
              Bu boş durum ZARARSIZ DEĞİL: kategori yoksa öğrenci ve
              veli hiç talep açamaz. Ekran bunu açıkça söylüyor,
              yoksa "talep gelmiyor" diye yorumlanır.
            */
            aciklama="Kategori tanımlanana kadar öğrenci ve veli hiçbir talep açamaz."
          />
        ) : (
          <ul className="flex flex-col gap-2">
            {liste.data.map((k) => (
              <li
                key={k.id}
                className="border-notr-100 flex flex-wrap items-center gap-2 border-b pb-2 last:border-0"
              >
                <span className="text-notr-900 text-sm font-medium">{k.name}</span>
                <code className="bg-notr-100 rounded px-1.5 py-0.5 font-mono text-xs">
                  {k.code}
                </code>
                <Rozet>
                  {k.openableBy === "STUDENT"
                    ? "Öğrenci açar"
                    : k.openableBy === "PARENT"
                      ? "Veli açar"
                      : "Öğrenci ve veli açar"}
                </Rozet>
                {k.description && (
                  <span className="text-notr-600 w-full text-sm">{k.description}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </Kart>

      {acik && (
        <Kart baslik="Yeni kategori">
          <div className="flex flex-col gap-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <Alan
                etiket="Kod"
                value={kod}
                onChange={(e) => setKod(e.target.value)}
                yardim="Küçük harf, rakam ve alt çizgi. Sonradan DEĞİŞMEZ — kayıtlar buna bağlanır."
              />
              <Alan
                etiket="Görünen ad"
                value={ad}
                onChange={(e) => setAd(e.target.value)}
                yardim="Öğrenci ve veli bunu görecek."
              />
            </div>

            <MetinAlani
              etiket="Açıklama"
              rows={2}
              value={aciklama}
              onChange={(e) => setAciklama(e.target.value)}
            />

            <Secim
              etiket="Bu kategoriyi kim karara bağlar"
              value={kararIzni}
              onChange={(e) => setKararIzni(e.target.value)}
              yardim="Kişi değil İZİN seçiliyor: kişi ayrılınca kural bozulmasın, izni taşıyan herkes karara bağlayabilsin."
            >
              {SIK_KULLANILAN_IZINLER.map(([izin, etiket]) => (
                <option key={izin} value={izin}>
                  {etiket}
                </option>
              ))}
            </Secim>

            <Secim
              etiket="Kim açabilir"
              value={acabilen}
              onChange={(e) => setAcabilen(e.target.value as "STUDENT" | "PARENT" | "BOTH")}
            >
              <option value="BOTH">Öğrenci ve veli</option>
              <option value="STUDENT">Yalnızca öğrenci</option>
              <option value="PARENT">Yalnızca veli</option>
            </Secim>

            {tanimla.isError && (
              <p role="alert" className="text-hata-500 text-sm font-medium">
                {tanimla.error instanceof ApiHatasi
                  ? tanimla.error.message
                  : "Kategori tanımlanamadı."}
              </p>
            )}
            {bilgi && (
              <p role="status" className="text-notr-700 text-sm">
                {bilgi}
              </p>
            )}

            <div>
              <Buton
                yukleniyor={tanimla.isPending}
                disabled={kod.trim().length < 2 || ad.trim().length < 2}
                onClick={() => {
                  setBilgi(null);
                  tanimla.mutate();
                }}
              >
                Kategoriyi tanımla
              </Buton>
            </div>
          </div>
        </Kart>
      )}
    </div>
  );
}

// =====================================================================
// REKLAM HARCAMASI — F-CRM-07
// =====================================================================

/**
 * Kaynak listesi ŞEMADAKİ `LeadSource` ile birebir.
 *
 * İlk yazımda "GOOGLE", "FACEBOOK", "TIKTOK", "WALK_IN" yazmıştım —
 * hiçbiri şemada yok. Canlı denemede sunucu "Invalid enum value"
 * dedi. Ekranın kendi listesini uydurması bu sınıfın klasik hatası:
 * form geçerli görünür, gönderilince reddedilir ve kullanıcı neyi
 * yanlış yaptığını anlamaz.
 *
 * `crm.controller.ts` bu enum'u `$Enums.LeadSource`tan alıyor; yani
 * şema değişirse sunucu kendiliğinden uyar, BU liste uymaz. Bir
 * kaynak eklendiğinde buraya da yazılmalı — bekçi test bunu tutuyor.
 */
const KAYNAK_ADI: Record<string, string> = {
  GOOGLE_ADS: "Google reklamı",
  META_ADS: "Meta reklamı",
  INSTAGRAM: "Instagram",
  WHATSAPP: "WhatsApp",
  ORGANIC: "Organik (arama)",
  REFERRAL: "Tavsiye",
  PHONE: "Telefon",
  MANUAL: "Elle giriş",
  OTHER: "Diğer",
};

/**
 * REKLAM HARCAMASI GİRİŞİ.
 *
 * Huni ekranı "kayıt başına maliyet" gösteriyor ama harcama girilecek
 * hiçbir yer yoktu — yani o sütun hiç dolmuyordu. Hesap yapılamıyordu,
 * ekran da bunu "0 TL" değil "hesaplanamadı" diye gösteriyordu; doğru
 * davranış, eksik olan girdiydi.
 */
export function ReklamHarcamasi() {
  const [acik, setAcik] = useState(false);
  const [kaynak, setKaynak] = useState("GOOGLE_ADS");
  const [kampanya, setKampanya] = useState("");
  const [baslangic, setBaslangic] = useState("");
  const [bitis, setBitis] = useState("");
  const [tutar, setTutar] = useState("");
  const [not, setNot] = useState("");
  const [bilgi, setBilgi] = useState<string | null>(null);

  const gir = useMutation({
    mutationFn: () =>
      harcamaGir({
        kaynak,
        kampanya: kampanya.trim() || null,
        baslangic,
        bitis,
        tutar: tutar.trim(),
        not: not.trim() || null,
      }),
    onSuccess: () => {
      setBilgi("Harcama kaydedildi. Huni ekranındaki maliyet bu dönem için hesaplanabilir.");
      setTutar("");
      setKampanya("");
      setNot("");
    },
  });

  // Tarih sırası ekranda da tutuluyor: sunucu da reddediyor ama
  // kullanıcıyı boş bir istekten önce uyarmak daha az sürtünme.
  const tarihBozuk = Boolean(baslangic && bitis && baslangic > bitis);

  if (!acik) {
    return (
      <div>
        <Buton gorunum="ikincil" onClick={() => setAcik(true)}>
          Reklam harcaması gir
        </Buton>
      </div>
    );
  }

  return (
    <Kart
      baslik="Reklam harcaması"
      aciklama="Kayıt başına maliyetin girdisi. Harcama girilmeden huni maliyeti hesaplanamaz."
    >
      <div className="flex flex-col gap-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <Secim etiket="Kaynak" value={kaynak} onChange={(e) => setKaynak(e.target.value)}>
            {Object.entries(KAYNAK_ADI).map(([k, a]) => (
              <option key={k} value={k}>
                {a}
              </option>
            ))}
          </Secim>
          <Alan
            etiket="Kampanya"
            value={kampanya}
            onChange={(e) => setKampanya(e.target.value)}
            yardim="İsteğe bağlı."
          />
          <Alan
            etiket="Dönem başlangıcı"
            type="date"
            value={baslangic}
            onChange={(e) => setBaslangic(e.target.value)}
          />
          <Alan
            etiket="Dönem bitişi"
            type="date"
            value={bitis}
            onChange={(e) => setBitis(e.target.value)}
            hata={tarihBozuk ? "Bitiş, başlangıçtan önce olamaz." : undefined}
          />
        </div>

        <Alan
          etiket="Tutar (TL)"
          inputMode="decimal"
          value={tutar}
          onChange={(e) => setTutar(e.target.value)}
          /*
            Tutar DİZGİ olarak gönderiliyor. Sayıya çevirip göndermek
            kuruş kaybına yol açardı — para bu projede `Decimal`
            (CLAUDE.md kural 8) ve kayan noktalı sayıya uğramamalı.
          */
          yardim="12345.67 biçiminde. Nokta ile, virgül değil."
        />

        <MetinAlani etiket="Not" rows={2} value={not} onChange={(e) => setNot(e.target.value)} />

        {gir.isError && (
          <p role="alert" className="text-hata-500 text-sm font-medium">
            {gir.error instanceof ApiHatasi ? gir.error.message : "Harcama kaydedilemedi."}
          </p>
        )}
        {bilgi && (
          <p role="status" className="text-notr-700 text-sm">
            {bilgi}
          </p>
        )}

        <div className="flex gap-2">
          <Buton
            yukleniyor={gir.isPending}
            disabled={!baslangic || !bitis || tarihBozuk || !/^\d+(\.\d{1,2})?$/.test(tutar.trim())}
            onClick={() => {
              setBilgi(null);
              gir.mutate();
            }}
          >
            Harcamayı kaydet
          </Buton>
          <Buton gorunum="sessiz" onClick={() => setAcik(false)}>
            Kapat
          </Buton>
        </div>
      </div>
    </Kart>
  );
}

// =====================================================================
// MEŞGULİYET KAYDI — matris §5.11
// =====================================================================

/**
 * ÖĞRENCİNİN OKUL/DERSHANE MEŞGULİYETİ.
 *
 * Program üretimi bu saatleri DOLU sayıyor. Kayıt yoksa sistem
 * öğrenciyi bütün gün boş varsayar ve okul saatine ödev yazar —
 * öğrencinin yapamayacağı bir program üretilir ve suç öğrencide
 * görünür.
 */
export function MesguliyetKaydi() {
  const [acik, setAcik] = useState(false);
  const [ogrenciId, setOgrenciId] = useState("");
  const [baslik, setBaslik] = useState("");
  const [baslangic, setBaslangic] = useState("");
  const [bitis, setBitis] = useState("");
  const [haftalik, setHaftalik] = useState(true);
  const [bilgi, setBilgi] = useState<string | null>(null);

  const ogrenciler = useQuery({
    queryKey: ["ogrenciler"],
    queryFn: ogrenciListesi,
    retry: false,
    enabled: acik,
  });

  const ekle = useMutation({
    mutationFn: () =>
      mesguliyetEkle({
        ogrenciId,
        baslik: baslik.trim(),
        baslangic,
        bitis,
        /*
          Tekrar kuralı RFC 5545. "Her hafta aynı saat" en sık durum —
          okul her hafta aynı saatte. Tek seferlik kayıt yazmak,
          kullanıcıya aynı formu her hafta doldurtmak olurdu.
        */
        tekrarKurali: haftalik ? "FREQ=WEEKLY" : null,
      }),
    onSuccess: () => {
      setBilgi("Meşguliyet kaydedildi. Program üretimi bu saatleri dolu sayacak.");
      setBaslik("");
      setBaslangic("");
      setBitis("");
    },
  });

  const tarihBozuk = Boolean(baslangic && bitis && baslangic >= bitis);

  if (!acik) {
    return (
      <div>
        <Buton gorunum="ikincil" onClick={() => setAcik(true)}>
          Meşguliyet ekle
        </Buton>
      </div>
    );
  }

  return (
    <Kart
      baslik="Öğrenci meşguliyeti"
      aciklama="Okul, dershane, kurs gibi öğrencinin çalışamayacağı saatler. Program üretimi bu saatleri dolu sayar."
    >
      <div className="flex flex-col gap-3">
        {ogrenciler.isError ? (
          <p role="alert" className="text-hata-500 text-sm font-medium">
            {ogrenciler.error instanceof ApiHatasi
              ? ogrenciler.error.message
              : "Öğrenci listesi alınamadı."}
          </p>
        ) : ogrenciler.isPending ? (
          <Iskelet satir={2} />
        ) : (
          <>
            <Secim
              etiket="Öğrenci"
              value={ogrenciId}
              onChange={(e) => setOgrenciId(e.target.value)}
            >
              <option value="">Seçin…</option>
              {ogrenciler.data.ogrenciler.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.ad} {o.soyad}
                </option>
              ))}
            </Secim>

            <Alan
              etiket="Ne"
              value={baslik}
              onChange={(e) => setBaslik(e.target.value)}
              yardim="“Okul”, “Dershane”, “Yüzme kursu” gibi."
            />

            <div className="grid gap-3 sm:grid-cols-2">
              <Alan
                etiket="Başlangıç"
                type="datetime-local"
                value={baslangic}
                onChange={(e) => setBaslangic(e.target.value)}
              />
              <Alan
                etiket="Bitiş"
                type="datetime-local"
                value={bitis}
                onChange={(e) => setBitis(e.target.value)}
                hata={tarihBozuk ? "Bitiş, başlangıçtan sonra olmalı." : undefined}
              />
            </div>

            <label className="min-h-dokunma flex cursor-pointer items-center gap-3">
              <input
                type="checkbox"
                className="accent-marka-600 size-5"
                checked={haftalik}
                onChange={(e) => setHaftalik(e.target.checked)}
              />
              <span className="text-notr-800 text-sm">Her hafta tekrar eder</span>
            </label>

            {ekle.isError && (
              <p role="alert" className="text-hata-500 text-sm font-medium">
                {ekle.error instanceof ApiHatasi ? ekle.error.message : "Kaydedilemedi."}
              </p>
            )}
            {bilgi && (
              <p role="status" className="text-notr-700 text-sm">
                {bilgi}
              </p>
            )}

            <div className="flex gap-2">
              <Buton
                yukleniyor={ekle.isPending}
                disabled={
                  !ogrenciId || baslik.trim().length < 2 || !baslangic || !bitis || tarihBozuk
                }
                onClick={() => {
                  setBilgi(null);
                  ekle.mutate();
                }}
              >
                Meşguliyeti kaydet
              </Buton>
              <Buton gorunum="sessiz" onClick={() => setAcik(false)}>
                Kapat
              </Buton>
            </div>
          </>
        )}
      </div>
    </Kart>
  );
}
